import { useEffect, useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useAuthedServerFn } from "@/lib/server-fn-auth";
import { getMemberBracketView, type MemberBracketView } from "@/lib/bracket.functions";
import { getAccessibleLeagueBySlug } from "@/lib/access.functions";
import { renderBracketPdf } from "@/lib/print-pdf.functions";
import { openPdfFromBase64 } from "@/lib/open-pdf";
import { hydrateMatchesForPrint, PrintablePage } from "@/components/PrintableBracket";
import { useAuth } from "@/lib/auth-context";
import "@/styles/print.css";

export const Route = createFileRoute("/print/$league/bracket/$bracketId")({
  component: PrintBracketPage,
  head: () => ({
    meta: [
      { title: "Print Bracket — Bracket Goblin" },
      { name: "robots", content: "noindex" },
    ],
  }),
});

function PrintBracketPage() {
  const { league: leagueSlug, bracketId } = Route.useParams();
  const { session, loading: authLoading } = useAuth();
  const fn = useAuthedServerFn(getMemberBracketView);
  const lookupLeague = useAuthedServerFn(getAccessibleLeagueBySlug);
  const renderPdfFn = useAuthedServerFn(renderBracketPdf);
  const [data, setData] = useState<MemberBracketView | null>(null);
  const [lockAtIso, setLockAtIso] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);

  const handleDownloadPdf = async () => {
    if (!data) return;
    setPdfLoading(true);
    setPdfError(null);
    try {
      const result = await renderPdfFn({
        data: { leagueId: data.league.id, targetEntryId: data.target.entryId },
      });
      openPdfFromBase64(result.pdfBase64, result.filename);
    } catch (e) {
      setPdfError(e instanceof Error ? e.message : "Failed to render PDF");
    } finally {
      setPdfLoading(false);
    }
  };

  useEffect(() => {
    if (authLoading || !session) return;
    let cancelled = false;
    (async () => {
      try {
        const lg = await lookupLeague({ data: { slug: leagueSlug } });
        if (cancelled) return;
        setLockAtIso(lg.bracket_lock_at);
        const view = await fn({
          data: { leagueId: lg.id, targetEntryId: bracketId },
        });
        if (!cancelled) setData(view);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : "Failed to load");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authLoading, session, leagueSlug, bracketId, fn, lookupLeague]);

  const matches = useMemo(
    () =>
      data
        ? hydrateMatchesForPrint(
            data.picks,
            data.teams,
            data.matches
              .filter((m) => m.round === "r32")
              .map((m) => ({
                match_code: m.match_code,
                home_team_id: m.home_team_id,
                away_team_id: m.away_team_id,
              })),
          )
        : [],
    [data],
  );

  if (authLoading) return null;
  if (!session) {
    return (
      <div className="print-helper">
        <span>You need to sign in to view this bracket.</span>
      </div>
    );
  }
  if (error) {
    return (
      <div className="print-helper" style={{ color: "#b91c1c" }}>
        <span>{error}</span>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="print-helper">
        <span>Loading bracket…</span>
      </div>
    );
  }

  return (
    <div className="print-root">
      <div className="print-helper">
        <span>
          {pdfError ? (
            <span style={{ color: "#b91c1c" }}>{pdfError}</span>
          ) : (
            <>
              One-click PDF (Letter, landscape) — or use{" "}
              <kbd>Ctrl</kbd>/<kbd>⌘</kbd> + <kbd>P</kbd>.
            </>
          )}
        </span>
        <span style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <button
            type="button"
            onClick={handleDownloadPdf}
            disabled={pdfLoading}
            style={{
              background: "#111",
              color: "#fff",
              border: "none",
              padding: "6px 12px",
              borderRadius: 4,
              fontSize: 12,
              fontWeight: 600,
              cursor: pdfLoading ? "wait" : "pointer",
              opacity: pdfLoading ? 0.6 : 1,
            }}
          >
            {pdfLoading ? "Generating PDF…" : "Download PDF"}
          </button>
          <span>{data.target.displayName}</span>
        </span>
      </div>
      <PrintablePage
        leagueName={data.league.name}
        memberDisplayName={data.target.displayName}
        memberRealName={data.target.realName}
        lockAtIso={lockAtIso}
        matches={matches}
        championTotalKnockoutGoals={data.championTotalKnockoutGoals}
        bonusPicks={data.bonusPicks}
        bonusQuestions={data.bonusQuestions.map((q) => ({
          id: q.id,
          question_label: q.question_label,
          short_label: q.short_label,
          threshold: q.threshold,
          over_label: q.over_label,
          under_label: q.under_label,
        }))}
        groups={data.groups}
        groupPicks={data.groupPicks}
      />
    </div>
  );
}
